import React from 'react';
import styled from 'styled-components';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  label?: string;
}

const SwitchContainer = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  user-select: none;
  opacity: ${props => props.$disabled ? 0.5 : 1};
`;

const SwitchTrack = styled.div<{ $checked: boolean; $size: string; $disabled: boolean }>`
  position: relative;
  display: inline-block;
  width: ${props => {
    switch (props.$size) {
      case 'small': return '32px';
      case 'large': return '56px';
      default: return '44px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'small': return '18px';
      case 'large': return '30px';
      default: return '24px';
    }
  }};
  background-color: ${props => 
    props.$checked 
      ? props.$disabled ? 'var(--primary-300)' : 'var(--primary-500)'
      : 'var(--gray-300)'
  };
  border-radius: 999px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => 
      !props.$disabled && (
        props.$checked 
          ? 'var(--primary-600)' 
          : 'var(--gray-400)'
      )
    };
  }
`;

const SwitchThumb = styled.div<{ $checked: boolean; $size: string }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%) translateX(${props => props.$checked ? 
    props.$size === 'small' ? '14px' : 
    props.$size === 'large' ? '26px' : 
    '20px' 
    : '2px'
  });
  width: ${props => {
    switch (props.$size) {
      case 'small': return '14px';
      case 'large': return '26px';
      default: return '20px';
    }
  }};
  height: ${props => {
    switch (props.$size) {
      case 'small': return '14px';
      case 'large': return '26px';
      default: return '20px';
    }
  }};
  background-color: white;
  border-radius: 50%;
  box-shadow: var(--shadow-sm);
  transition: transform 0.2s ease;
`;

const SwitchInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const SwitchLabel = styled.span`
  margin-left: var(--spacing-sm);
  font-size: var(--font-size-md);
  color: var(--text-primary);
`;

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = 'medium',
  label,
}) => {
  const handleChange = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleChange();
    }
  };

  return (
    <SwitchContainer $disabled={disabled}>
      <SwitchInput
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-checked={checked}
        role="switch"
      />
      <SwitchTrack 
        $checked={checked} 
        $size={size} 
        $disabled={disabled}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
      >
        <SwitchThumb $checked={checked} $size={size} />
      </SwitchTrack>
      {label && <SwitchLabel>{label}</SwitchLabel>}
    </SwitchContainer>
  );
};